<template>
    <el-table
        :data="cvList"
        v-loading="loading"
        class="rcv-table"
        empty-text="Chưa có hồ sơ ứng viên nào"
        stripe
        highlight-current-row
    >
        <!-- ID -->
        <el-table-column label="#" width="70" align="center">
            <template slot-scope="{ row }">
                <span class="rcv-table__id">#{{ row.id }}</span>
            </template>
        </el-table-column>

        <!-- Thông tin ứng viên -->
        <el-table-column label="Thông tin ứng viên" min-width="240">
            <template slot-scope="{ row }">
                <div class="rcv-table__name" @click="$emit('view', row)">
                    <i class="el-icon-user"></i>
                    <span>{{ row.name || "Chưa có tên" }}</span>
                    <span
                        v-if="row.source_type"
                        class="rcv-source-tag"
                        :class="Number(row.source_type) === 1 ? 'rcv-source-tag--apply' : 'rcv-source-tag--web'"
                        :title="Number(row.source_type) === 1 ? 'Ứng viên tự apply' : 'Lấy trên mạng'"
                    >
                        {{ Number(row.source_type) === 1 ? 'Tự apply' : 'Trên mạng' }}
                    </span>
                </div>
                <div class="rcv-table__contacts">
                    <span v-if="row.phone" class="rcv-table__contact-item">
                        <i class="el-icon-phone-outline"></i>
                        <a :href="`tel:${row.phone}`" @click.stop>{{ row.phone }}</a>
                        <i class="el-icon-document-copy rcv-copy-icon" title="Sao chép SĐT" @click.stop="copyText(row.phone)" />
                    </span>
                    <span v-if="row.email" class="rcv-table__contact-item">
                        <i class="el-icon-message"></i>
                        <a :href="`mailto:${row.email}`" @click.stop>{{ row.email }}</a>
                        <i class="el-icon-document-copy rcv-copy-icon" title="Sao chép Email" @click.stop="copyText(row.email)" />
                    </span>
                </div>
                <div v-if="row.address" class="rcv-table__address" :title="row.address">
                    <i class="el-icon-location-outline"></i>
                    <span>{{ row.address }}</span>
                </div>
            </template>
        </el-table-column>

        <!-- Vị trí / Job -->
        <el-table-column label="Vị trí / Order" min-width="180">
            <template slot-scope="{ row }">
                <div class="rcv-table__job">
                    <span v-if="row.job_id || row.order_id" class="rcv-table__job-tag">
                        Job #{{ row.job_id || row.order_id }}
                    </span>
                    <span v-else class="rcv-table__muted">Chưa gắn Job</span>
                </div>
                <div v-if="jobTitle(row)" class="rcv-table__job-title" :title="jobTitle(row)">
                    {{ jobTitle(row) }}
                </div>
                <div class="rcv-table__type-tag" :class="`rcv-table__type-tag--${row.type}`">
                    {{ typeLabel(row.type) }}
                </div>
            </template>
        </el-table-column>

        <!-- Tiến trình / Trạng thái ứng viên -->
        <el-table-column label="Tiến trình ứng viên" min-width="190">
            <template slot-scope="{ row }">
                <div class="rcv-table__status" @click="$emit('view', row)">
                    <el-tooltip
                        :content="`[#${row.status}] ${statusMeta(row.status).label}`"
                        placement="top"
                        :open-delay="200"
                    >
                        <span
                            class="rcv-status-badge"
                            :style="{
                                color: statusMeta(row.status).color,
                                backgroundColor: statusMeta(row.status).bg,
                                borderColor: statusMeta(row.status).color,
                            }"
                        >
                            <i :class="statusMeta(row.status).icon"></i>
                            <span class="rcv-status-badge__text">{{ statusMeta(row.status).label }}</span>
                        </span>
                    </el-tooltip>
                </div>
            </template>
        </el-table-column>

        <!-- Lời nhắn ứng tuyển (Message) -->
        <el-table-column label="Lời nhắn ứng tuyển" min-width="200">
            <template slot-scope="{ row }">
                <div v-if="row.message" class="rcv-table__msg" :title="row.message" @click="$emit('view', row)">
                    {{ row.message }}
                </div>
                <span v-else class="rcv-table__muted">— Không có lời nhắn —</span>
            </template>
        </el-table-column>

        <!-- File CV -->
        <el-table-column label="File CV / Đính kèm" width="170" align="center">
            <template slot-scope="{ row }">
                <div v-if="row.cv_file || (row.media && row.media.length)" class="rcv-table__cv">
                    <el-button
                        size="mini"
                        type="primary"
                        plain
                        icon="el-icon-document"
                        class="rcv-cv-btn"
                        @click="$emit('preview-cv', row)"
                    >
                        Xem CV
                    </el-button>
                    <a
                        v-if="row.cv_file"
                        :href="row.cv_file"
                        target="_blank"
                        download
                        class="rcv-cv-download"
                        title="Tải file CV về máy"
                        @click.stop
                    >
                        <i class="el-icon-download"></i>
                    </a>
                    <el-badge
                        v-if="row.media && row.media.length > 1"
                        :value="`+${row.media.length - 1}`"
                        type="info"
                        class="rcv-media-badge"
                        :title="`Ứng viên có ${row.media.length} tệp đính kèm`"
                    />
                </div>
                <span v-else class="rcv-table__muted">Chưa có CV</span>
            </template>
        </el-table-column>

        <!-- Thời gian nộp -->
        <el-table-column label="Thời gian nộp" width="160">
            <template slot-scope="{ row }">
                <div class="rcv-table__time">{{ formatDate(row.created_at) }}</div>
                <div class="rcv-table__time-ago">{{ fromNow(row.created_at) }}</div>
            </template>
        </el-table-column>

        <!-- Thao tác -->
        <el-table-column label="Thao tác" width="165" fixed="right" align="center">
            <template slot-scope="{ row }">
                <div class="rcv-table__actions">
                    <!-- Nút Gửi duyệt CV cho HR: Bỏ khi status >= Phỏng vấn V1 (PHONG_VAN_V1 = 5) -->
                    <el-tooltip
                        v-if="isHrDepartment && Number(row.status) < PHONG_VAN_V1 && (Number(row.status) === 0 || row.hr_approve !== 1)"
                        content="Gửi Duyệt CV: Chuyển Teamlead duyệt"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            type="success"
                            icon="el-icon-s-promotion"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-preliminary', row)"
                        />
                    </el-tooltip>

                    <!-- Nút đánh giá PV V1 cho Teamlead khi ở trạng thái PV V1 (5) -->
                    <el-tooltip v-if="Number(row.status) === 5" content="Điền kết quả PV V1 (Teamlead)" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            type="primary"
                            icon="el-icon-chat-dot-round"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-interview', { candidate: row, round: 1 })"
                        />
                    </el-tooltip>


                    <!-- Nút Điền thông tin Thử việc & Offer cho Teamlead khi ở trạng thái Cần điền Offer (13) -->
                    <el-tooltip
                        v-else-if="!isHrDepartment && Number(row.status) === 13"
                        content="Điền thông tin Thử việc & Offer (Teamlead)"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            type="success"
                            icon="el-icon-edit-outline"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-offer', row)"
                        />
                    </el-tooltip>

                    <!-- Nút Nhập kết quả Đào tạo hội nhập cho HR đối với hồ sơ Tuyển chính thức ở trạng thái Đã gửi ĐT hội nhập (16) -->
                    <el-tooltip
                        v-else-if="isHrDepartment && Number(row.status) === 16 && isOfficialRecruitment(row)"
                        content="Nhập kết quả đào tạo hội nhập (HR)"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            type="primary"
                            icon="el-icon-reading"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-training-evaluation', row)"
                        />
                    </el-tooltip>

                    <!-- Nút xác nhận đi làm cho HR khi ở trạng thái Cần làm thủ tục Pass (14), Ứng viên không đi làm (15) hoặc Đã gửi đào tạo hội nhập (16) -->
                    <el-tooltip
                        v-else-if="isHrDepartment && (Number(row.status) === 14 || Number(row.status) === 15 || Number(row.status) === 16)"
                        :content="Number(row.status) === 14 ? 'Xác nhận đi làm / Không đi làm (HR)' : 'Cập nhật lại trạng thái đi làm (HR)'"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            :type="Number(row.status) === 14 ? 'success' : (Number(row.status) === 16 ? 'warning' : 'info')"
                            icon="el-icon-s-custom"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-work-confirmation', { candidate: row, hasWork: Number(row.status) === 16 || Number(row.status) === 14 })"
                        />
                    </el-tooltip>

                    <!-- Nút đánh giá thử việc cho Teamlead khi ở trạng thái Cần đánh giá thử việc (22) hoặc đã nhập kết quả (23, 24) -->
                    <el-tooltip
                        v-else-if="!isHrDepartment && (Number(row.status) === 22 || Number(row.status) === 23 || Number(row.status) === 24)"
                        :content="Number(row.status) === 22 ? 'Điền kết quả thử việc (Teamlead)' : 'Sửa kết quả thử việc (Teamlead)'"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            :type="Number(row.status) === 24 ? 'danger' : 'success'"
                            icon="el-icon-medal"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('open-probation', row)"
                        />
                    </el-tooltip>

                    <!-- Nút xác nhận Đã nhận việc cho HR khi ở trạng thái Đạt thử việc (23) hoặc Cần đánh giá thử việc (22) -->
                    <el-tooltip
                        v-else-if="isHrDepartment && (Number(row.status) === 23 || Number(row.status) === 22)"
                        content="Xác nhận Đã nhận việc (HR)"
                        placement="top"
                        :open-delay="200"
                    >
                        <el-button
                            size="mini"
                            type="success"
                            icon="el-icon-trophy"
                            class="rcv-action-btn rcv-action-btn--pv"
                            @click="$emit('mark-hired', row)"
                        />
                    </el-tooltip>

                    <el-tooltip content="Xem chi tiết & CV" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            type="primary"
                            plain
                            icon="el-icon-view"
                            class="rcv-action-btn"
                            @click="$emit('view', row)"
                        />
                    </el-tooltip>
                    <el-tooltip content="Chỉnh sửa thông tin" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            type="info"
                            plain
                            icon="el-icon-edit"
                            class="rcv-action-btn"
                            @click="$emit('edit', row)"
                        />
                    </el-tooltip>
                    <el-tooltip content="Xoá hồ sơ" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            type="danger"
                            plain
                            icon="el-icon-delete"
                            class="rcv-action-btn"
                            @click="$emit('delete', row)"
                        />
                    </el-tooltip>
                </div>
            </template>
        </el-table-column>
    </el-table>
</template>

<script>
import moment from "moment-timezone";
import { getStatusMeta, getStatusText, PHONG_VAN_V1, DA_NHAN_VIEC } from "@/core/config/recruitmentCvs/recruitmentCvsOption";

export default {
    name: "CvTable",
    props: {
        cvList: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        orders: { type: Array, default: () => [] },
        isHrDepartment: { type: Boolean, default: true },
    },
    data() {
        return {
            PHONG_VAN_V1,
            DA_NHAN_VIEC,
        };
    },
    methods: {
        statusMeta(status) {
            return getStatusMeta(status);
        },
        statusText(status) {
            return getStatusText(status);
        },
        jobTitle(jobIdOrRow) {
            if (!jobIdOrRow) return "";
            if (typeof jobIdOrRow === "object") {
                const found = this.orders.find((o) =>
                    String(o.id) === String(jobIdOrRow.job_id) ||
                    String(o.id) === String(jobIdOrRow.order_id) ||
                    String(o.id) === String(jobIdOrRow.raw_job_id) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrRow.job_id)) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrRow.raw_job_id))
                );
                return found ? (found.title || found.position_title || "") : (jobIdOrRow.order_title || "");
            }
            const found = this.orders.find((o) =>
                String(o.id) === String(jobIdOrRow) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrRow))
            );
            return found ? (found.title || found.position_title || "") : "";
        },
        typeLabel(type) {
            const num = Number(type);
            if (num === 1) return "Tuyển hộ (Giáo viên)";
            return "Tuyển chính thức";
        },
        isOfficialRecruitment(cand) {
            if (!cand) return true;
            if (Number(cand.type) === 1) return false;
            const found = (this.orders || []).find((o) =>
                String(o.id) === String(cand.job_id) ||
                String(o.id) === String(cand.order_id) ||
                String(o.id) === String(cand.raw_job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(cand.job_id)) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(cand.raw_job_id))
            );
            if (found && (Number(found.type) === 2 || String(found.type).toLowerCase() === "teacher")) {
                return false;
            }
            return true;
        },
        formatDate(v) {
            return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—";
        },
        fromNow(v) {
            return v ? moment(v).fromNow() : "";
        },
        copyText(text) {
            if (!text) return;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$message.success(`Đã sao chép: ${text}`);
                });
            } else {
                const input = document.createElement("input");
                input.value = text;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
                this.$message.success(`Đã sao chép: ${text}`);
            }
        },
    },
};
</script>

<style scoped>
.rcv-table__id {
    font-weight: 700;
    color: #4f46e5;
    font-size: 13px;
}
.rcv-table__name {
    font-weight: 700;
    color: #1e293b;
    font-size: 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color 0.15s;
}
.rcv-table__name:hover {
    color: #4f46e5;
}
.rcv-table__name i {
    color: #6366f1;
}
.rcv-source-tag {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 4px;
    margin-left: 6px;
    display: inline-flex;
    align-items: center;
}
.rcv-source-tag--apply {
    background: #eef2ff;
    color: #4f46e5;
    border: 1px solid #c7d2fe;
}
.rcv-source-tag--web {
    background: #f0fdf4;
    color: #16a34a;
    border: 1px solid #bbf7d0;
}
.rcv-table__contacts {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-top: 4px;
}
.rcv-table__contact-item {
    font-size: 12px;
    color: #475569;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.rcv-table__contact-item a {
    color: #475569;
    text-decoration: none;
    transition: color 0.15s;
}
.rcv-table__contact-item a:hover {
    color: #4f46e5;
    text-decoration: underline;
}
.rcv-copy-icon {
    cursor: pointer;
    font-size: 11px;
    color: #94a3b8;
    margin-left: 2px;
    transition: color 0.15s;
}
.rcv-copy-icon:hover {
    color: #4f46e5;
}
.rcv-table__address {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
}
.rcv-table__job {
    display: flex;
    align-items: center;
    gap: 6px;
}
.rcv-table__job-tag {
    display: inline-block;
    padding: 2px 8px;
    background: #eef2ff;
    color: #4338ca;
    border-radius: 6px;
    font-size: 11.5px;
    font-weight: 700;
}
.rcv-table__job-title {
    font-size: 12px;
    color: #334155;
    font-weight: 500;
    margin-top: 3px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.rcv-table__type-tag {
    display: inline-block;
    font-size: 11px;
    color: #64748b;
    margin-top: 3px;
}
.rcv-table__type-tag--1 {
    color: #b45309;
}
.rcv-table__msg {
    font-size: 12.5px;
    color: #475569;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    cursor: pointer;
    background: #f8fafc;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid #f1f5f9;
}
.rcv-table__msg:hover {
    background: #f1f5f9;
    color: #1e293b;
}
.rcv-table__cv {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.rcv-cv-btn {
    padding: 5px 8px !important;
    font-size: 11.5px !important;
    border-radius: 6px !important;
}
.rcv-cv-download {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #64748b;
    text-decoration: none;
    transition: all 0.15s;
}
.rcv-cv-download:hover {
    background: #e2e8f0;
    color: #1e293b;
}
.rcv-media-badge /deep/ .el-badge__content {
    background-color: #6366f1;
    color: #fff;
    font-size: 10px;
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
    border: none;
}
.rcv-table__time {
    font-size: 12.5px;
    color: #334155;
    font-weight: 500;
}
.rcv-table__time-ago {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 2px;
}
.rcv-table__muted {
    font-size: 12px;
    color: #94a3b8;
    font-style: italic;
}
.rcv-table__actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.rcv-table__status {
    display: flex;
    align-items: center;
    cursor: pointer;
    width: fit-content;
}
.rcv-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 11.5px;
    font-weight: 600;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    line-height: 1.3;
    max-width: 100%;
}
.rcv-status-badge:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}
.rcv-status-badge i {
    font-size: 12px;
    flex-shrink: 0;
}
.rcv-status-badge__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rcv-action-btn {
    padding: 6px 8px !important;
    font-size: 12px;
    margin: 0 !important;
    border-radius: 6px !important;
}
</style>
